import { Autocomplete, TextField, CircularProgress, Box } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useMemo } from "react";

const CREATE_OPTION_ID = "__create__";

export interface CreateOption {
  id: typeof CREATE_OPTION_ID;
  __isCreate: true;
  label: string;
}

function isCreateOption<T>(option: T | CreateOption): option is CreateOption {
  return (option as CreateOption)?.__isCreate === true;
}

export interface AsyncAutocompleteWithQuickCreateProps<T extends { id: string }> {
  value: string;
  onChange: (id: string | null) => void;
  options: T[];
  getOptionLabel: (option: T) => string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Label for the "Create new" option (e.g. "Create new Location") */
  createLabel: string;
  /** Called when user selects the create option; parent opens QuickCreateModal */
  onOpenQuickCreate: () => void;
  /** Optional error text */
  error?: string;
  /** Optional helper text */
  helperText?: string;
}

/**
 * Autocomplete that supports search + a "Create new X" option.
 * When user selects the create option, onOpenQuickCreate() is called; parent opens modal and on success sets new entity as selected.
 */
export default function AsyncAutocompleteWithQuickCreate<T extends { id: string }>({
  value,
  onChange,
  options,
  getOptionLabel,
  label,
  placeholder,
  required = false,
  disabled = false,
  loading = false,
  createLabel,
  onOpenQuickCreate,
  error,
  helperText,
}: AsyncAutocompleteWithQuickCreateProps<T>) {
  const createOption: CreateOption = useMemo(
    () => ({ id: CREATE_OPTION_ID, __isCreate: true, label: createLabel }),
    [createLabel]
  );

  const allOptions = useMemo(() => [...options, createOption], [options, createOption]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    if (value === CREATE_OPTION_ID) return null;
    return options.find((o) => o.id === value) ?? null;
  }, [options, value]);

  const handleChange = (_: unknown, newValue: T | CreateOption | null) => {
    if (isCreateOption(newValue)) {
      onOpenQuickCreate();
      return;
    }
    onChange(newValue ? (newValue as T).id : null);
  };

  return (
    <Autocomplete<T | CreateOption>
      value={selectedOption as T | null}
      onChange={handleChange}
      options={allOptions}
      getOptionLabel={(option) =>
        isCreateOption(option) ? option.label : getOptionLabel(option as T)
      }
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(option, val) => {
        if (isCreateOption(option)) return false;
        return (option as T).id === (val as T)?.id;
      }}
      renderOption={(props, option) => {
        if (isCreateOption(option)) {
          return (
            <Box
              component="li"
              {...props}
              key={CREATE_OPTION_ID}
              sx={{ borderTop: 1, borderColor: "divider", fontWeight: 600 }}
            >
              <AddOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
              {option.label}
            </Box>
          );
        }
        return (
          <Box component="li" {...props} key={(option as T).id}>
            {getOptionLabel(option as T)}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
